import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, FilterChip, Modal, MultiSelect, Search, Switch, Tag, Textarea, Checkbox, Tooltip } from '@indipia/ui-core';
import { COMM_METHOD_DIRECT_LABEL, isDirectRecord, useAppStore, useAuthStore } from '@indipia/shared';
import { SiteRegistrationPopup, StepD, type StepBResult, assigneeSummaryText, useMineOnlyState } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import { applyMineOnly, matchesSiteQuery } from '../utils/siteAssignees';
import { useWorklogStore } from '../store/worklogStore';
import { PHOTO_POLICY, REPORT_CONTENT_MAX, pickValidPhotos, photoDropNotice } from '../config/uploadPolicy';
import { Toast } from '../components/Toast';
import { StatusChip } from '../components/StatusChip';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { ThumbImg } from '../components/ThumbImg';
import { IndiPhotoPickerModal, type PickedIndiPhoto } from '../components/IndiPhotoPickerModal';
import { INDI_PHOTO_AGENT_ID, PhotoAttach, revokePreview, type LocalPhoto } from '../components/OrderAttachments';
import { CustReqOwnerRows } from '../components/custReq/CustReqOwnerRows';
import { useOwnerCustReq } from '../components/custReq/useOwnerCustReq';
import { CUST_REQ_REPORT_MESSAGE, saveErrorMessage } from '../components/custReq/custReqReport.logic';
import type {
  BasicInfoWorklog,
  FieldReportOwnerPhotoMeta,
  SiteOption,
  WorkOrderSiteInfo,
} from '../types/worklog.types';

/** 작업상태(REQ-2-03) — 진행중/작업완료 2택1. 이슈발생은 독립 토글(issueOn)이라 여기 포함되지 않는다. */
type WorkStatus = 'ongoing' | 'done';

/**
 * 현장보고 작성(사장님) — 사장님 콘솔 (기능명세 C-2, API명세 §3.3·§4.1.8).
 *
 * 현장 선택 시 §2.12 site-info로 날씨·공종을 세팅하고, 공종(1개 이상)·진행상태(REQ-2-03)·이슈(REQ-2-04)·
 * 보고내용·사진(파일+인디포토, REQ-2-05)을 담아 §3.3으로 등록한다. 반장 작성(Z-1, §3.9)과 저장 페이로드는
 * 대부분 동일하나 인증·현장식별·작성주체·인디포토 허용 여부가 다르다.
 */

const WORK_LOG_AGENT_ID = 2; // 인디공사현황 numericAgentId (현장등록 팝업용)

/** 주소 파라미터(`?scheduleId=&taskId=`) 값 — 양의 정수가 아니면 무시한다. */
function parsePositiveInt(raw: string | null): number | null {
  return raw && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
function todayParts(): { date: string; weekday: string; dow: number } {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`,
    weekday: WEEKDAYS[d.getDay()],
    dow: d.getDay(),
  };
}

const extOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

// ─────────────────────────────────────────────────────────────────────────
// 공사기간 입력 모달 — site만 있는 현장(worklogId=null) 선택 시 Step D(공사기간)만 단독으로 띄운다.
// 작업요청 작성(WorkOrderCreatePage)과 동일 컴포넌트 — 전체 현장등록 마법사 없이 shared-features StepD 재사용.
// ─────────────────────────────────────────────────────────────────────────
function ConstructionPeriodModal({
  siteId,
  siteName,
  onClose,
  onComplete,
}: {
  siteId: number;
  siteName: string | null;
  onClose: () => void;
  onComplete: (siteId: number) => void;
}) {
  // StepD는 siteId로 fetchSite()를 다시 조회해 실제 주소·동·호를 채우므로, 나머지 필드는 빈 값으로 넘겨도 된다.
  const stepBResult: StepBResult = {
    siteId,
    bldgId: null,
    roadAddress: '',
    sitePyeong: null,
    squareMeters: null,
    bldgName: null,
    addressDong: null,
    addressHo: null,
  };
  return (
    <Modal open onClose={onClose} title="공사기간을 지정해주세요." size="lg">
      <Alert
        kind="info"
        className="mb-04"
        icon={
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        }
      >
        <p className="text-body-sm leading-relaxed">
          <b className="font-bold">{siteName || '선택하신 현장'}</b>은(는) 공사현황에서 처음 사용하는
          현장이에요. <br />공사 <b>시작일과 종료일을 선택하고 저장</b>하면, 바로 현장보고를 작성할 수 있어요.
        </p>
      </Alert>
      <StepD
        stepBResult={stepBResult}
        onComplete={(id) => onComplete(id)}
        onClose={onClose}
      />
    </Modal>
  );
}

export function FieldReportOwnerWritePage() {
  // worklog 있는 현장 + site만 있는 현장 병합 목록. BE(basic-info §5.3 siteOptions)가 노출 대상 필터·정렬을
  // 이미 확정해 내려주므로(작업요청 작성화면과 동일 기준) FE는 재필터·재정렬하지 않는다.
  const siteOptions = useWorklogStore((s) => s.siteOptions);
  const storeStatus = useWorklogStore((s) => s.status);
  const memberId = useAuthStore((s) => s.user?.memberId ?? null);

  // 화면 진입 시 basic-info 강제 재조회 — worklog.scheduleId 를 sv-schedule 최신 상태로 맞춘다.
  // WorkOrderCreatePage 와 동일 사유(§2.12 site-info 조회 화면). 상세 근거는 그쪽 주석 참조.
  useEffect(() => {
    void useWorklogStore.getState().refresh();
  }, []);

  const today = todayParts();
  const weekdayColor =
    today.dow === 0 ? 'var(--color-error)' : today.dow === 6 ? 'var(--color-info)' : 'var(--shell-on-surface)';

  const [selected, setSelected] = useState<BasicInfoWorklog | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  // 진행상태(REQ-2-03, 진행중/작업완료 2택1) — 미선택 저장 허용이라 기본 null
  const [workStatus, setWorkStatus] = useState<WorkStatus | null>(null);
  // 이슈발생(REQ-2-04) — 작업상태와 독립된 토글
  const [issueOn, setIssueOn] = useState(false);
  const [issueCodes, setIssueCodes] = useState<string[]>([]);
  // 검수(SIF-3 C-2) — 사장님·영업사원이 직접 쓰는 작업현황이라 공종과 무관하게 기본 체크(본인 작성 = 검수까지 완료).
  // 이슈발생 ON 이면 '조치완료'로 바뀐다. 풀고 저장하면 작업현황만 등록(검수·조치는 작업보고 목록에서)
  const [inspectDone, setInspectDone] = useState(true);
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [indiPhotoOpen, setIndiPhotoOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [pendingSite, setPendingSite] = useState<BasicInfoWorklog | null>(null);
  // site만 있는 현장(worklogId=null) 선택 시 schedule-lookup 조회 중 — 자동생성/팝업 분기 대기(작업요청 작성과 동일)
  const [scheduleLookupLoading, setScheduleLookupLoading] = useState(false);
  // schedule-lookup 결과 공사기간이 불완전해 공사기간 입력 팝업(Step D)을 띄워야 하는 현장
  const [periodPopupSite, setPeriodPopupSite] = useState<SiteOption | null>(null);

  // 공종 마스터(ZEU_WORK_TYPE) — '+ 공종 추가' 팝업용
  const workTypeMaster = useWorkTypeName();

  // 이슈유형 마스터(A02_FIELD_ISSUE_TYPE, REQ-2-04) — worklogStore 전용 코드그룹(§9.1)
  const worklogCodes = useWorklogStore((s) => s.codes);
  const issueTypeMaster = useMemo(
    () => worklogCodes.filter((c) => c.codeGroupCode === 'A02_FIELD_ISSUE_TYPE'),
    [worklogCodes],
  );

  const indiPhotoSubscribed = useAppStore((s) => s.subscriptions.some((sub) => sub.agentId === INDI_PHOTO_AGENT_ID));

  // 현장 선택 → site-info(날씨 + 스케줄 공종) 조회(D-03 표준 TanStack Query 전환).
  const siteInfoQuery = useQuery({
    queryKey: ['work-log', 'field-report-owner', 'siteInfo', selected?.siteId],
    queryFn: async ({ signal }) =>
      (
        await worklogApi.getWorkOrderSiteInfo(
          { siteId: selected!.siteId, siteAddress: selected!.siteAddress, siteLat: selected!.siteLat, siteLng: selected!.siteLng },
          signal,
        )
      ).data.data,
    enabled: selected != null,
  });
  const siteInfo: WorkOrderSiteInfo | null = selected ? siteInfoQuery.data ?? null : null;
  // 조회 동안 공종 영역을 '조회 중'으로 잠가, 사용자가 미리 고른 뒤 재설정되는 혼란을 막는다.
  const loadingSite = selected != null && siteInfoQuery.isFetching;

  // 스케줄 공종을 후보로(중복 제거). 미연동이면 빈 목록 — '+ 공종 추가'로 채운다.
  const scheduleTypes = useMemo(() => {
    const seen = new Set<string>();
    const types: { code: string; name: string }[] = [];
    for (const wt of siteInfo?.schedule?.workTypes ?? []) {
      if (!wt.workTypeCode || seen.has(wt.workTypeCode)) continue;
      seen.add(wt.workTypeCode);
      types.push({ code: wt.workTypeCode, name: wt.workTypeName ?? wt.workTypeCode });
    }
    return types;
  }, [siteInfo]);
  // 공종 선택 옵션(MultiSelect, D-09 §15.47) — 스케줄 후보를 먼저, 그 외 공종마스터 전체를 뒤에 붙인다.
  const workTypeOptions = useMemo(() => {
    const scheduleCodes = new Set(scheduleTypes.map((t) => t.code));
    const rest = [...workTypeMaster.entries()]
      .filter(([code]) => !scheduleCodes.has(code))
      .map(([code, name]) => ({ value: code, label: name }));
    return [...scheduleTypes.map((t) => ({ value: t.code, label: t.name })), ...rest];
  }, [scheduleTypes, workTypeMaster]);

  // 결과만 직접등록(SIF-3) 시공파트너 공종 — 공종 목록 배지 기준(스케줄 공정의 연락 방식 'N')
  const directCodes = useMemo(
    () => new Set((siteInfo?.schedule?.workTypes ?? []).filter((wt) => isDirectRecord(wt.commYn)).map((wt) => wt.workTypeCode)),
    [siteInfo],
  );

  // 고객 요청(S16) — 공종 1개가 스케줄의 공정 1개로 확정될 때만 요청 영역을 보인다(확정결정 25). scheduleTypes 는 코드 중복을 제거해
  // taskId 가 유실되므로 쓰지 않고, 스케줄 공정 원본으로 확정한다.
  const custReq = useOwnerCustReq({
    selectedCodes,
    scheduleWorkTypes: siteInfo?.schedule?.workTypes,
  });

  // 현장 변경 시 선택 초기화(공종·진행상태·이슈) — site-info는 쿼리가 알아서 다시 로드한다.
  useEffect(() => {
    setSelectedCodes([]);
    setWorkStatus(null);
    setIssueOn(false);
    setIssueCodes([]);
    setInspectDone(true);
    setLinkedTask(null);   // 다른 현장에서는 지정 공정을 쓰지 않는다(아래 링크 공정 선택은 이 초기화 뒤에 다시 채운다)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.worklogId]);

  // ── 인디플래너 공종 상세창 [작업현황 등록 ›](SIF-3 결과만 직접등록)에서 연 경우: `?scheduleId=&taskId=` ──
  // 작업요청 작성의 `?siteId=&taskId=` 와 같은 규칙 — 모르는 값 · 열 수 없는 현장(진행중 목록에 없음)은 무시하고 평소처럼 연다.
  // 현장 · 공정 미리 선택은 각각 한 번만 적용한다(사용자가 현장을 바꾸거나 해제한 뒤 주소 값이 다시 끼어들지 않게).
  // 이 화면의 site-info 는 날짜 필터 없이 스케줄 공정 전체를 받으므로 지정 공정이 목록에 있다.
  const [searchParams] = useSearchParams();
  const linkScheduleId = parsePositiveInt(searchParams.get('scheduleId'));
  const linkTaskId = linkScheduleId != null ? parsePositiveInt(searchParams.get('taskId')) : null;
  const worklogs = useWorklogStore((s) => s.worklogs);
  const linkSiteApplied = useRef(false);
  const linkTaskApplied = useRef(false);
  // 주소로 지정해 고른 공정 — 그 공종만 고른 채 저장하면 이 공정에 보고를 붙인다(작업일이 공사기간 밖이어도 공정 진행 요약에 잡히게)
  const [linkedTask, setLinkedTask] = useState<{ taskId: number; workTypeCode: string } | null>(null);
  useEffect(() => {
    if (linkSiteApplied.current || linkScheduleId == null) return;
    if (selected != null) {
      linkSiteApplied.current = true; // 사용자가 먼저 현장을 골랐으면 주소는 따르지 않는다
      return;
    }
    const w = worklogs.find((x) => x.scheduleId === linkScheduleId);
    if (!w) return; // 기준정보가 아직 안 왔을 수 있다 — 도착하면 다시 시도
    linkSiteApplied.current = true;
    setSelected(w);
  }, [linkScheduleId, worklogs, selected]);
  // 공정 — 주소로 고른 현장의 site-info 가 도착하면 그 공정의 공종을 선택한다(현장 변경 초기화 뒤에 실행되도록 이 위치에 둔다)
  useEffect(() => {
    if (linkTaskApplied.current || linkTaskId == null || selected == null || loadingSite || !siteInfo) return;
    if (selected.scheduleId !== linkScheduleId) return;
    linkTaskApplied.current = true;
    const linked = siteInfo.schedule?.workTypes?.find((wt) => wt.taskId === linkTaskId);
    if (linked) {
      setSelectedCodes([linked.workTypeCode]);
      setLinkedTask({ taskId: linked.taskId, workTypeCode: linked.workTypeCode });
    }
  }, [linkTaskId, linkScheduleId, selected, loadingSite, siteInfo]);

  // objectURL 정리(언마운트 시)
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach(revokePreview), []);

  const toggleIssueCode = (code: string) =>
    setIssueCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const addPhotos = (files: File[]) => {
    const result = pickValidPhotos(files, photos.length);
    if (result.accepted.length) {
      setPhotos((p) => [
        ...p,
        ...result.accepted.map((f): LocalPhoto => {
          const url = URL.createObjectURL(f);
          return { id: url, url, name: f.name, source: 'FILE', file: f };
        }),
      ]);
    }
    const notice = photoDropNotice(result);
    if (notice) setToastMsg(notice);
  };

  /** 인디포토 사진첩에서 고른 사진 첨부(REQ-2-05) — 파일 업로드 없이 photoId 로 등록된다. */
  const addIndiPhotos = (picked: PickedIndiPhoto[]) => {
    setIndiPhotoOpen(false);
    if (!picked.length) return;
    const room = Math.max(0, PHOTO_POLICY.maxCount - photos.length);
    const within = picked.filter((p) => !photos.some((x) => x.photoId === p.photoId)).slice(0, room);
    if (!within.length) return;
    setPhotos((prev) => [
      ...prev,
      ...within.map((p): LocalPhoto => ({
        id: `indi-${p.photoId}`,
        url: p.thumbUrl ?? '',
        name: `인디포토 ${p.photoId}`,
        source: 'INDI_PHOTO',
        photoId: p.photoId,
        fileId: p.fileId ?? null,
      })),
    ]);
    setToastMsg(`인디포토 사진 ${within.length}장을 첨부했습니다.`);
  };

  const removePhoto = (id: string) =>
    setPhotos((p) => {
      const t = p.find((x) => x.id === id);
      if (t) revokePreview(t);
      return p.filter((x) => x.id !== id);
    });

  const resetForm = () => {
    photos.forEach(revokePreview);
    setPhotos([]);
    setSelectedCodes([]);
    setWorkStatus(null);
    setIssueOn(false);
    setIssueCodes([]);
    setInspectDone(true);
    setContent('');
    custReq.reset();
  };

  // 초기화 — 현장·공종·사진·보고내용 전부 리셋(현장 해제 시 site-info 효과가 공종·날씨도 정리).
  const clearAll = () => {
    setSelected(null);
    resetForm();
  };

  // 작성 중(내용·사진·공종·진행상태·이슈)인데 현장을 바꾸면 확인 후 초기화 — 작업요청 작성과 동일 UX.
  const dirty =
    content.trim().length > 0 || photos.length > 0 || selectedCodes.length > 0 || workStatus != null || issueOn;
  const chooseSite = (w: BasicInfoWorklog) => {
    if (selected && selected.worklogId !== w.worklogId && dirty) {
      setPendingSite(w);
      return;
    }
    setSelected(w);
  };
  const confirmChangeSite = () => {
    if (pendingSite) {
      resetForm();
      setSelected(pendingSite);
      setPendingSite(null);
    }
  };

  // basic-info 재조회 후 새로 생성된 worklog를 찾아 선택 (작업요청 작성과 동일)
  const selectCreatedWorklog = async (siteId: number) => {
    await useWorklogStore.getState().refresh();
    const w = useWorklogStore.getState().worklogs.find((x) => x.siteId === siteId);
    if (w) chooseSite(w);
  };

  /**
   * site만 있는 현장(worklogId=null) 선택 — schedule-lookup 조회 후 분기(작업요청 작성과 동일).
   * 공사기간(시작·종료일)이 둘 다 있으면 팝업 없이 바로 공사현황을 생성하고,
   * 하나라도 없으면 공사기간 입력 팝업(Step D)을 띄운다.
   */
  const handleSelectSiteOnly = async (opt: SiteOption) => {
    if (memberId == null) {
      alert('로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요.');
      return;
    }
    setScheduleLookupLoading(true);
    try {
      const res = await worklogApi.getScheduleLookup(opt.siteId);
      const period = res.data.data;
      if (period.constStartDate && period.constEndDate) {
        await worklogApi.createWorklog({
          siteId: opt.siteId,
          memberId,
          scheduleId: period.scheduleId ?? null,
          constStartDate: period.constStartDate,
          constEndDate: period.constEndDate,
        });
        await selectCreatedWorklog(opt.siteId);
      } else {
        setPeriodPopupSite(opt);
      }
    } catch {
      alert('공사기간 조회에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setScheduleLookupLoading(false);
    }
  };

  // 공사기간 입력 팝업(Step D) 완료 → 새로 생성된 worklog 선택
  const handlePeriodPopupComplete = async (siteId: number) => {
    setPeriodPopupSite(null);
    await selectCreatedWorklog(siteId);
  };

  const saveMutation = useMutation({
    mutationFn: (worklogId: number) => {
      const meta: FieldReportOwnerPhotoMeta[] = photos.map((p, i) => {
        if (p.source === 'INDI_PHOTO') {
          // fileId 는 인디포토 목록이 내려줄 때만 담긴다 — 있으면 photoId 와 함께 저장(FILE_ID·PHOTO_ID 동시 등록).
          return {
            photoSeq: i,
            source: 'INDI_PHOTO',
            photoId: p.photoId!,
            ...(p.fileId != null ? { fileId: p.fileId } : {}),
          };
        }
        return {
          photoSeq: i,
          source: 'FILE',
          fileName: p.name,
          fileExt: extOf(p.name),
          fileSize: p.file!.size,
        };
      });
      const photoFiles = photos.filter((p) => p.source === 'FILE' && p.file).map((p) => p.file as File);
      return worklogApi.createFieldReport(worklogId, {
        workTypeCodes: selectedCodes,
        issueTypeCodes: issueOn ? issueCodes : undefined,
        workCmplYn: workStatus === 'done' ? 'Y' : workStatus === 'ongoing' ? 'N' : undefined,
        reportContent: content.trim(),
        photos: meta.length ? meta : undefined,
        photoFiles: photoFiles.length ? photoFiles : undefined,
        ...(custReq.taskId != null
          ? { taskId: custReq.taskId, customerRequests: custReq.buildInputs() }
          // 인디플래너 [작업현황 등록 ›]으로 지정한 공정을 그대로(그 공종 하나만) 저장 — 그 공정에 붙인다
          : linkedTask && selectedCodes.length === 1 && selectedCodes[0] === linkedTask.workTypeCode
            ? { taskId: linkedTask.taskId }
            : {}),
        // 검수 줄 — 체크면 검수완료(이슈면 조치완료)까지 함께 처리, 풀면 검수 전으로 저장
        inspectYn: inspectDone ? 'Y' : 'N',
      });
    },
    onSuccess: () => { setDone(true); custReq.afterSave(); },
    onError: (err) => setToastMsg(
      saveErrorMessage(err, custReq.taskId != null, '현장보고 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.'),
    ),
  });
  const saving = saveMutation.isPending;

  const canSave = !!selected && selectedCodes.length > 0 && content.trim().length > 0 && !saving;
  // 검수 줄 이름 — 이슈발생 ON 이면 조치완료(SIF-3 C-2)
  const inspectLabel = issueOn ? '조치완료' : '검수완료';
  const hint = !selected
    ? '현장을 선택해주세요'
    : selectedCodes.length === 0
      ? '공종을 1개 이상 선택해주세요'
      : content.trim().length === 0
        ? '보고 내용을 입력하면 저장할 수 있어요'
        : inspectDone
          ? `저장하면 작업현황 등록 + ${inspectLabel}가 함께 처리돼요`
          : '저장할 수 있어요';

  const save = () => {
    if (!selected || !canSave) return;
    saveMutation.mutate(selected.worklogId);
  };

  const weather = siteInfo?.weatherNow;
  // 스케줄 연동 현장을 조회 중 — 조회중 안내(지속 info 토스트)로 대기 유도
  const scheduleLoading = loadingSite && selected?.scheduleId != null;

  return (
    <div className="flex h-full flex-col bg-surface">
      {scheduleLookupLoading ? (
        <Toast
          message="공사기간을 조회 중입니다. 잠시만 기다려 주세요."
          variant="info"
          duration={null}
          onClose={() => {}}
        />
      ) : scheduleLoading ? (
        <Toast
          message="스케줄 정보를 조회 중입니다. 잠시만 기다려 주세요."
          variant="info"
          duration={null}
          onClose={() => {}}
        />
      ) : (
        <Toast message={toastMsg} onClose={() => setToastMsg(null)} />
      )}

      {/* 상단 메타(날짜 + 오늘 날씨) — 계층 브레드크럼은 셸 TopBar */}
      <div className="flex shrink-0 flex-wrap items-center gap-04 px-06 pt-05 text-body-md">
        <span className="font-medium">
          <span className="text-text">{today.date} </span>
          <span style={{ color: weekdayColor }}>({today.weekday})</span>
        </span>
        {weather && weather.tempNow != null && (
          <span className="flex items-center gap-03 text-text-secondary">
            {weather.weatherIcon && <span className="text-base leading-none">{weather.weatherIcon}</span>}
            {weather.weatherName && <span>{weather.weatherName}</span>}
            <span className="font-semibold text-text">{weather.tempNow}°</span>
            {weather.tempHigh != null && <span className="text-error">↑ {weather.tempHigh}°</span>}
            {weather.tempLow != null && <span className="text-[color:var(--color-infoStrong)]">↓ {weather.tempLow}°</span>}
          </span>
        )}
      </div>

      {/* 현장 선택 + 액션 (풀폭 한 줄) — 작업요청 작성과 동일 배치 */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-05 gap-y-03 px-06 pb-04 pt-03">
        <SiteSelect
          siteOptions={siteOptions}
          status={storeStatus}
          selected={selected}
          onSelectWorklog={chooseSite}
          onSelectSiteOnly={handleSelectSiteOnly}
        />
        {!selected && (
          <span
            className="inline-flex shrink-0 items-center gap-sm rounded-full px-04 py-02 text-caption font-medium"
            style={{ background: 'var(--color-infoSubtle)', border: '1px solid var(--color-info)', color: 'var(--color-info)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="13" />
              <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
            </svg>
            현장을 선택해주세요.
          </span>
        )}
        <div className="ml-auto flex items-center gap-03">
          <Button variant="etc" size="sm" onClick={clearAll} disabled={saving}>↩ 초기화</Button>
          <Button variant="success" size="sm" disabled={!canSave} onClick={save}>{saving ? '저장 중…' : '저장'}</Button>
        </div>
      </div>

      {/* 작성 폼 — 남은 높이 채움(풀폭) */}
      <div className="flex min-h-0 flex-1 flex-col px-06 pb-05">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface-raised">
          {/* 공종 선택 — 현장 선택 시 스케줄 공종을 목록으로, 사용자가 직접 다중선택 */}
          <Row label="공종 선택">
            {!selected ? (
              <span className="mt-02 text-body-sm text-text-muted">현장을 먼저 선택하세요.</span>
            ) : loadingSite ? (
              // 조회 중 잠금 — 미리 선택 후 재설정되는 혼란 방지
              <span className="mt-01 flex items-center gap-03 text-body-sm text-text-secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="animate-spin" aria-hidden>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {selected.scheduleId != null ? '스케줄 정보를 조회 중입니다…' : '현장 정보를 조회 중입니다…'}
              </span>
            ) : (
              <div className="flex flex-1 flex-col gap-sm">
                {/* D-09 §15.47 MultiSelect — 설계서 §5.4.4·§5.5.2 명시 컴포넌트(신규 등록, WorkOrderCreatePage 참조인 선택과 동일 패턴) */}
                <MultiSelect
                  className="flex-1"
                  searchable
                  values={selectedCodes}
                  onChange={setSelectedCodes}
                  options={workTypeOptions}
                  placeholder="공종을 선택하세요"
                  // 결과만 직접등록 시공파트너 공종은 목록 항목에 배지(SIF-3 C-2)
                  renderOption={(opt) => directCodes.has(opt.value)
                    ? <span className="inline-flex items-center gap-sm">{opt.label}<Badge status="warning">{COMM_METHOD_DIRECT_LABEL}</Badge></span>
                    : opt.label}
                />
                {/* 화면기획서 실측(현장보고_작성_사장님_웹.html .gong-hint) — 스케줄 연동 현장에서만 노출 */}
                {selected.scheduleId != null && (
                  <span
                    className="inline-flex w-fit items-center gap-sm rounded-full px-04 py-02 text-caption font-medium"
                    style={{ background: 'var(--color-infoSubtle)', border: '1px solid var(--color-info)', color: 'var(--color-info)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="13" />
                      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
                    </svg>
                    공사스케줄 기준 공종입니다. 필요한 항목을 선택하세요.
                  </span>
                )}
              </div>
            )}
          </Row>

          {/* 고객 요청(S16) — 공정이 확정된 공종에 전달된 요청이 있을 때만(미확정이면 영역 숨김, 안내 문구 없음) */}
          {custReq.visible && (
            <Row label="고객 요청">
              <CustReqOwnerRows ctl={custReq} />
            </Row>
          )}
          {custReq.loadFailed && (
            <Row label="고객 요청">
              <span className="mt-02 text-body-sm text-text-muted">{CUST_REQ_REPORT_MESSAGE.loadFail}</span>
            </Row>
          )}

          {/* 진행상태(REQ-2-03) — 진행중/작업완료 2택1, 미선택 저장 허용 */}
          <Row label="진행상태">
            <div className="flex flex-1 flex-wrap items-center gap-sm">
              <FilterChip size="sm" active={workStatus === 'ongoing'} onClick={() => setWorkStatus('ongoing')}>
                진행중
              </FilterChip>
              <FilterChip size="sm" active={workStatus === 'done'} onClick={() => setWorkStatus('done')}>
                작업완료
              </FilterChip>
            </div>
          </Row>

          {/* 이슈발생(REQ-2-04) — 작업상태와 독립된 토글. 켜면 이슈 항목 칩이 나타난다 */}
          <Row label="이슈발생">
            <div className="flex flex-1 flex-wrap items-center gap-sm">
              <Switch
                checked={issueOn}
                onChange={(e) => {
                  const on = e.target.checked;
                  setIssueOn(on);
                  if (!on) setIssueCodes([]);
                }}
              />
              {issueOn &&
                issueTypeMaster.map((t) => (
                  <FilterChip key={t.codeValue} size="sm" active={issueCodes.includes(t.codeValue)} onClick={() => toggleIssueCode(t.codeValue)}>
                    {t.codeName}
                  </FilterChip>
                ))}
            </div>
          </Row>

          {/* 사진첨부(REQ-2-05) — 파일 + 인디포토 */}
          <Row label="사진첨부">
            <PhotoAttach
              photos={photos}
              onAdd={addPhotos}
              onRemove={removePhoto}
              onOpen={setLightbox}
              onOpenIndiPhoto={() => setIndiPhotoOpen(true)}
            />
          </Row>

          {/*
            보고 내용 — 표준 Textarea 의 fill 변형(.field-fill). 남은 높이를 채우고 resize 를 막는 처리가
            .field-fill .textarea 규칙에 들어 있어, 수제 클래스(w-full flex-1 resize-none)가 필요 없다.
          */}
          <div className="flex min-h-0 flex-1 flex-col p-05">
            <Textarea
              fill
              textareaClassName="min-h-[160px]"
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, REPORT_CONTENT_MAX))}
              placeholder="현장 시공 현황을 입력하세요..."
            />
          </div>

          {/* 검수(SIF-3 C-2) — 공종과 무관하게 늘 표시 · 기본 체크, 이슈발생 ON 이면 '조치완료'. 저장하면 함께 처리 */}
          <Row label="검수">
            <Checkbox
              checked={inspectDone}
              onChange={(e) => setInspectDone(e.target.checked)}
              label={inspectLabel}
            />
          </Row>

          {/* 힌트 — 화면기획서 실측: 이모지 대신 말풍선 SVG 아이콘(라벨·아이콘에 이모지 미사용 원칙) */}
          <div className="flex shrink-0 items-center gap-03 border-t border-border px-05 py-03 text-body-sm text-text-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{hint}</span>
          </div>
        </div>
      </div>

      {/* 사진 뷰어 — 파일 첨부분은 objectURL이라 기본 <img>, 인디포토분은 인증 필요한 서버 URL이라 blob 로더로 렌더 */}
      <PhotoLightbox
        photos={photos.map((p) => ({ url: p.url }))}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
        renderImage={(photo, style) =>
          photo.url.startsWith('blob:') ? (
            <img src={photo.url} alt="" style={style} draggable={false} />
          ) : (
            <ThumbImg url={photo.url} imgStyle={style} />
          )
        }
      />

      {/* 인디포토 사진첩 선택(REQ-2-05) */}
      <IndiPhotoPickerModal
        open={indiPhotoOpen}
        siteId={selected?.siteId ?? null}
        siteName={selected?.siteName}
        subscribed={indiPhotoSubscribed}
        room={Math.max(0, PHOTO_POLICY.maxCount - photos.length)}
        onClose={() => setIndiPhotoOpen(false)}
        onConfirm={addIndiPhotos}
      />

      {/* 공사기간 입력 — site만 있는 현장 선택 시(schedule-lookup 공사기간 불완전) */}
      {periodPopupSite != null && (
        <ConstructionPeriodModal
          siteId={periodPopupSite.siteId}
          siteName={periodPopupSite.siteName}
          onClose={() => setPeriodPopupSite(null)}
          onComplete={handlePeriodPopupComplete}
        />
      )}

      {/* 현장 변경 확인 */}
      <Modal open={pendingSite != null} onClose={() => setPendingSite(null)} title="현장을 변경하시겠습니까?">
        <p className="text-body-md leading-relaxed text-text-secondary">
          현장을 변경하면 작성 중인 내용과 첨부 사진, 공종 선택이 초기화됩니다.
        </p>
        <div className="mt-05 flex justify-end gap-03">
          <Button variant="secondary" size="sm" onClick={() => setPendingSite(null)}>취소</Button>
          <Button size="sm" onClick={confirmChangeSite}>변경하기</Button>
        </div>
      </Modal>

      {/* 저장 완료 — 화면기획서 실측: 목록으로 이동하지 않고 폼만 초기화(같은 화면에서 다음 현장 이어서 등록) */}
      <Modal open={done} onClose={() => setDone(false)} title="저장 완료!">
        <div className="px-01 py-02 text-center">
          <div
            className="mx-auto flex items-center justify-center"
            style={{ width: 48, height: 48, borderRadius: 'var(--radius-full)', background: 'var(--color-successSubtle)' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p className="mt-03 text-body-sm leading-relaxed text-text-secondary">작업현황이 성공적으로 저장되었습니다.</p>
          <Button
            size="sm"
            className="mt-05 w-full"
            onClick={() => {
              setDone(false);
              resetForm();
            }}
          >
            확인
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/** 라벨 + 내용 한 줄(작업요청 작성과 동일 Row) */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-04 border-b border-border px-05 py-04">
      <span className="mt-02 w-10 shrink-0 text-body-sm font-semibold text-text-secondary">{label}</span>
      {children}
    </div>
  );
}

// ── 현장 선택 드롭다운 (worklogStore.siteOptions — BE가 이미 정렬·필터링해 내려줌) ──────
function SiteSelect({
  siteOptions, status, selected, onSelectWorklog, onSelectSiteOnly,
}: {
  siteOptions: SiteOption[];
  status: string;
  selected: BasicInfoWorklog | null;
  onSelectWorklog: (w: BasicInfoWorklog) => void;
  onSelectSiteOnly: (opt: SiteOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [regOpen, setRegOpen] = useState(false);

  // '☑ 내 현장만'(2026-10-12 개선사항 D) — 직원은 기본 체크, 사장님은 기본 해제. 해제하면 모든 시공중 현장 선택 가능.
  // 목록은 내 현장 먼저(그룹 없음), 검색은 현장명 · 고객명 · 담당자·관리자 이름.
  const [mineOnly, setMineOnly] = useMineOnlyState();
  const list = applyMineOnly(siteOptions, mineOnly).filter((o) => matchesSiteQuery(o, q));

  async function handleRegistered(siteId: number) {
    setRegOpen(false);
    try {
      // 공사현황 생성은 등록 팝업 Step D(공사기간)가 담당 — 여기서 만들면 중복 생성된다.
      await useWorklogStore.getState().refresh();
      const w = useWorklogStore.getState().worklogs.find((x) => x.siteId === siteId);
      if (w) onSelectWorklog(w);
    } catch {
      alert('현장 등록에 실패했습니다. 다시 시도해주세요.');
    }
  }

  return (
    <>
    <div className={`field${open ? ' combo open' : ' combo'}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center gap-03"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="shrink-0 text-text-muted">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
        <span className={selected ? 'flex-1 truncate text-left font-medium' : 'flex-1 truncate text-left text-text-muted'}>
          {selected ? selected.siteName ?? '(현장명 없음)' : '현장을 선택하세요'}
        </span>
        <span className="caret">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-dropdown" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute left-0 top-full z-dropdown mt-02 w-[320px] max-w-[calc(100vw-48px)] overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg">
            <div className="border-b border-border p-03">
              <Search autoFocus size="sm" value={q} onChange={setQ} placeholder="현장명, 고객명, 담당자 검색" />
            </div>
            <div className="max-h-64 overflow-auto">
              {status === 'loading' && <div className="py-06 text-center text-body-sm text-text-muted">불러오는 중…</div>}
              {status === 'error' && <div className="py-06 text-center text-body-sm text-text-muted">현장 목록을 불러오지 못했습니다.</div>}
              {status === 'ready' && list.length === 0 && <div className="py-06 text-center text-body-sm text-text-muted">{mineOnly && !q ? '내가 담당·관리하는 진행중 현장이 없습니다' : '검색 결과 없음'}</div>}
              {list.map((o) => (
                <button
                  key={`${o.worklogId ?? 'site'}-${o.siteId}`}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setQ('');
                    if (o.worklogId != null) {
                      const w = useWorklogStore.getState().worklogs.find((x) => x.worklogId === o.worklogId);
                      if (w) onSelectWorklog(w);
                    } else {
                      onSelectSiteOnly(o);
                    }
                  }}
                  className="flex w-full items-center gap-03 border-b border-border px-04 py-03 text-left last:border-b-0 hover:bg-surface-muted"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body-sm font-semibold text-text">{o.siteName ?? '(현장명 없음)'}</span>
                    {/* 둘째 줄: 고객명 + 담당 ○○ 외 N · 관리 ○○(지정 순서 첫 번째, 미지정이면 '담당자 미지정') — 회색 캡션, 강조 없음 */}
                    <span className="flex min-w-0 items-center gap-md text-caption">
                      <span className="truncate text-text-secondary">{o.customerName ?? '-'}</span>
                      <span className="truncate text-text-muted">
                        {assigneeSummaryText(o.chargeMembers, o.managerMembers)}
                      </span>
                    </span>
                  </span>
                  {o.progressStatus ? (
                    <StatusChip code={o.progressStatus} />
                  ) : (
                    <Tag size="sm" className="shrink-0 font-bold">
                      {o.siteStatusName}
                    </Tag>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); setRegOpen(true); }}
              className="flex w-full items-center gap-03 border-t border-dashed border-group/40 bg-group-subtle px-04 py-04 text-caption font-bold text-group hover:bg-group-subtle/70"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              공사현장 추가하기
            </button>
          </div>
        </>
      )}

      {regOpen && (
        <SiteRegistrationPopup
          agentId={WORK_LOG_AGENT_ID}
          onClose={() => setRegOpen(false)}
          onComplete={(siteId) => handleRegistered(siteId)}
        />
      )}
    </div>
    {/* ☑ 내 현장만 — 현장 셀렉박스 오른쪽(D) */}
    <Tooltip tip="내가 현장 담당자 또는 현장 관리자인 현장만 보기" tabIndex={-1} className="shrink-0">
      <Checkbox
        label="내 현장만"
        checked={mineOnly}
        onChange={(e) => setMineOnly(e.target.checked)}
      />
    </Tooltip>
    </>
  );
}

